import { Loader2 } from 'lucide-react';

import { PONTO_DE_PARTIDA as C } from './copy';

/** `6,4` → `6,4.º`; sin decimales si es entero. */
function posicao(valor) {
  if (valor == null) return null;
  const n = Math.round(valor * 10) / 10;
  return `${String(n).replace('.', ',')}.º`;
}

/**
 * Las cuatro cifras (valor y texto) de su ficha, con `?` mientras no hay
 * diagnóstico. Las pinta este bloque y la primera pantalla de la V6
 * (`HeroPersonalizado`).
 */
export function cartoesDe(gmb, d) {
  const pronto = !!d && d.estado === 'pronto';
  const posicaoTexto = pronto && d.posicaoMedia == null ? C.cartoes.foraTop : C.cartoes.posicao;
  return [
    { valor: pronto ? (posicao(d.posicaoMedia) || '20+') : '?.º', texto: posicaoTexto },
    // Sin cifra con el escaneo hecho = no sale en el top 20 de ningún punto:
    // tiene por delante más de 20 en todas partes.
    { valor: pronto ? (d.porDelante != null ? d.porDelante : '20+') : '?',
      texto: gmb ? C.cartoes.porDelante : C.cartoes.porDelanteSemGmb },
    { valor: pronto && d.seoScore != null ? `${d.seoScore}/100` : '?/100', texto: C.cartoes.score },
    gmb
      ? { valor: pronto && d.resenasSemResposta != null ? d.resenasSemResposta : '?', texto: C.cartoes.semResposta }
      : { valor: pronto && d.resenasTotal != null ? d.resenasTotal : '?', texto: C.cartoes.total },
  ];
}

function Cartao({ valor, texto, apagado = false }) {
  return (
    <div className="flex flex-col gap-1.5 rounded-[18px] bg-[#f5f5f7] p-[18px] md:gap-2 md:rounded-[22px] md:p-6">
      <span className={`text-[32px] font-extrabold tracking-[-1px] md:text-[44px] md:tracking-[-1.5px] ${apagado ? 'text-[#c7c7cc]' : 'text-[#1d1d1f]'}`}>
        {valor}
      </span>
      <span className="text-sm leading-snug text-[#4a4a52] md:text-[15px]">{texto}</span>
    </div>
  );
}

/**
 * «O teu ponto de partida»: las cuatro cifras de su ficha.
 *
 * - Con la ficha conectada: posición media en el mapa, negocios por delante,
 *   SEO Score de la app y avaliações por responder.
 * - Sin ficha: el diagnóstico que lanzó desde aquí (posición, por delante,
 *   score público y TOTAL de avaliações — las sin respuesta necesitan la ficha
 *   conectada). Sin diagnóstico, interrogantes y el buscador (`children`).
 *
 * @param {{ gmb: boolean, diagnostico: object|null, negocio: string,
 *           children?: React.ReactNode }} props
 */
export default function PontoDePartida({ gmb, diagnostico, negocio, children }) {
  const d = diagnostico;
  const pronto = d && d.estado === 'pronto';

  const titulo = gmb || pronto ? C.tituloGmb(d?.palavraChave) : C.tituloSemGmb;
  const sub = gmb || pronto ? C.subGmb(d?.negocio || negocio) : C.subSemGmb;

  const cartoes = cartoesDe(gmb, d);

  return (
    <section className="mx-auto flex w-full max-w-[640px] flex-col gap-4 px-5 pb-9 pt-11 md:max-w-[1160px] md:gap-5 md:px-10 md:py-[72px]">
      <p className="m-0 text-[13px] font-semibold uppercase tracking-[0.6px] text-[#9a5700] md:text-sm md:font-bold">{C.kicker}</p>
      <h2 className="m-0 text-[28px] font-extrabold leading-tight tracking-[-0.8px] md:max-w-[820px] md:text-[40px] md:leading-[1.1] md:tracking-[-1.2px]">{titulo}</h2>
      <p className="m-0 text-[15px] text-[#5e5e66] md:text-[17px]">{sub}</p>

      {children}

      <div className="grid grid-cols-2 gap-3 md:mt-2 md:grid-cols-4 md:gap-4">
        {cartoes.map((c) => (
          <Cartao key={c.texto} valor={c.valor} texto={c.texto} apagado={!pronto} />
        ))}
      </div>

      {gmb && d?.estado === 'a_calcular' && (
        <p className="m-0 flex items-center gap-2 text-sm text-[#5e5e66]">
          <Loader2 className="h-4 w-4 animate-spin text-[#feae2c]" aria-hidden="true" />
          {C.aCalcular}
        </p>
      )}
      {gmb && d?.estado === 'sem_mapa' && <p className="m-0 text-sm text-[#5e5e66]">{C.semMapa}</p>}
      {!gmb && pronto && <p className="m-0 text-[13px] leading-snug text-[#5e5e66]">{C.notaScorePublico}</p>}
      {!gmb && !pronto && !children && (
        <p className="m-0 text-sm text-[#5e5e66]">{C.resultadoAqui}</p>
      )}
    </section>
  );
}
